import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Home, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchJSON } from "@/lib/api";

interface StockThreshold {
  product: string;
  minThreshold: number;
  currentStock: number;
}

export default function MinimumStock() {
  const [showCount, setShowCount] = useState<string>("All");
  const [filterText, setFilterText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Fetch lubricants data using backend API
  const { data: lubricants = [], isLoading } = useQuery({
    queryKey: ["/api/lubricants"],
    queryFn: async () => {
      const response = await fetchJSON('/api/lubricants');
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to fetch lubricants');
      return result.rows || [];
    },
  });

  const thresholds = useMemo(() => {
    return lubricants.map((lub: any) => ({
      product: lub.lubricant_name || lub.name || lub.product_name || lub.product_code || 'Unknown',
      minThreshold: parseFloat(lub.minimum_stock || 0),
      currentStock: parseFloat(lub.current_stock || 0),
    }));
  }, [lubricants]);

  // Filtered rows
  const filteredThresholds = useMemo(() => {
    return thresholds.filter((item: StockThreshold) => 
      item.product.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [thresholds, filterText]);

  // Reset page on filter
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  // Pagination calculations
  const totalEntries = filteredThresholds.length;
  const pageSize = showCount === "All" ? totalEntries : parseInt(showCount, 10);
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));
  
  const paginatedThresholds = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredThresholds.slice(startIdx, startIdx + pageSize);
  }, [filteredThresholds, currentPage, pageSize]);

  const startEntry = totalEntries === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(totalEntries, currentPage * pageSize);

  // Export functions
  const toCSV = (rows: StockThreshold[]): string => {
    if (!rows.length) return "";
    const headers = ["Product", "Minimum Stock", "Present Stock"];
    const escape = (val: any) => {
      if (val === null || val === undefined) return "";
      const s = String(val).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [headers.join(","), ...rows.map(r => [escape(r.product), r.minThreshold.toFixed(2), r.currentStock.toFixed(2)].join(","))];
    return lines.join("\n");
  };

  const downloadCSV = () => {
    const csvContent = toCSV(filteredThresholds);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "minimum_stock.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const headers = ["Product", "Minimum Stock", "Present Stock"];
    const tableHtml = `
      <h2>Minimum Stock Report</h2>
      <table style="border-collapse: collapse; width: 100%; font-family: system-ui;">
        <thead>
          <tr style="background-color: #f5f5f5;">
            ${headers.map(h => `<th style="border: 1px solid #ddd; padding: 8px; text-align: left;">${h}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${filteredThresholds.map(r => `
            <tr>
              <td style="border: 1px solid #ddd; padding: 8px;">${r.product}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">${r.minThreshold.toFixed(2)}</td>
              <td style="border: 1px solid #ddd; padding: 8px; color: ${r.currentStock < r.minThreshold ? 'red' : 'black'}">${r.currentStock.toFixed(2)}</td>
            </tr>`).join("")}
        </tbody>
      </table>`;
    
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Minimum Stock Report</title></head><body onload="window.print(); window.close();">${tableHtml}</body></html>`);
    w.document.close();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Home className="h-4 w-4 text-black" />
        <span className="font-semibold text-black">Dashboard</span>
        <span>&gt;</span>
        <span className="text-blue-600 font-medium">Lubricants Stock</span>
      </div>

      {/* Banner */}
      <div className="bg-indigo-600 text-white py-8 px-6 rounded-md shadow-sm">
        <h2 className="text-2xl font-bold tracking-wide">Minimum Stock</h2>
      </div>

      <div className="bg-white rounded-lg border p-6 space-y-6 shadow-sm">
        {/* Controls Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Show:</span>
            <Select value={showCount} onValueChange={(val) => { setShowCount(val); setCurrentPage(1); }}>
              <SelectTrigger className="w-[100px] bg-white border border-gray-200">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={downloadCSV}
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 font-medium px-4"
              >
                CSV
              </Button>
              <Button 
                variant="outline" 
                onClick={downloadPDF}
                className="border-rose-600 text-rose-600 hover:bg-rose-50 hover:text-rose-700 font-medium px-4"
              >
                PDF
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filter:</span>
              <div className="relative w-48">
                <Input 
                  type="text" 
                  placeholder="Type to filter..." 
                  value={filterText} 
                  onChange={(e) => setFilterText(e.target.value)} 
                  className="bg-white border border-gray-200 pr-8"
                />
                <Search className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-md">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : (
            <Table>
              <TableHeader className="bg-gray-50/50 border-b">
                <TableRow>
                  <TableHead className="w-16 font-semibold text-gray-800">S.No</TableHead>
                  <TableHead className="font-semibold text-gray-800">Product</TableHead>
                  <TableHead className="font-semibold text-gray-800 text-right">Minimum Stock</TableHead>
                  <TableHead className="font-semibold text-gray-800 text-right">Present Stock</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedThresholds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center p-8 text-muted-foreground">
                      No matching records found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedThresholds.map((item, idx) => {
                    const serialNumber = (currentPage - 1) * pageSize + idx + 1;
                    const isLow = item.currentStock < item.minThreshold;
                    return (
                      <TableRow key={idx} className="hover:bg-gray-50/40 border-b">
                        <TableCell className="text-gray-600 font-medium">{serialNumber}</TableCell>
                        <TableCell className="font-medium text-gray-900">{item.product}</TableCell>
                        <TableCell className="text-right text-gray-700">{item.minThreshold.toFixed(2)}</TableCell>
                        <TableCell className={`text-right ${isLow ? 'text-rose-600 font-bold' : 'text-gray-900 font-medium'}`}>
                          {item.currentStock.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
          <div className="text-sm text-gray-600">
            Showing {startEntry} to {endEntry} of {totalEntries} entries
          </div>

          <div className="flex items-center gap-1 self-end sm:self-auto">
            <Button
              variant="outline"
              size="icon"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                onClick={() => setCurrentPage(page)}
                className={`h-8 w-8 p-0 text-xs font-semibold ${
                  currentPage === page ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'text-gray-700'
                }`}
              >
                {page}
              </Button>
            ))}

            <Button
              variant="outline"
              size="icon"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
